<template>
  <a-modal
    :visible="value"
    :title="isEditMode ? 'Edit category' : 'Add category'"
    width="445px"
    destroyOnClose
    @cancel="$emit('close')"
  >
    <template slot="default">
      <div class="category-modal">
        <LanguageSelect
          v-model="selectedLanguage"
          :langs="langs"
          :contentFilled="filledLangs"
          :error="formErrorState"
          short-button-label
          :style="{ top: 0 }"
          @copyFromDefault="handleCopyFromDefault"
          @clear="handleClearLang"
        />
        <a-form-model :rules="rules" :model="currentForm" ref="categoryForm">
          <a-form-model-item label="Title" prop="title" :colon="false">
            <a-input
              v-model="currentForm.title"
              size="large"
              placeholder="Category title"
            />
          </a-form-model-item>

          <a-form-model-item label="Icon" prop="selectedIcon" :colon="false">
            <InputSearchable
              v-model="currentForm.iconField"
              :items="icons"
              placeholder="Select icon"
              item-key="sport_id"
              label-key="name"
              list-style="grid"
              @search="handleIconSearch"
              @blur="handleIconSearch"
              @select="handleSelectIcon"
            >
              <template #prefix>
                <span class="prefix">
                  <img
                    v-if="currentForm.selectedIcon"
                    :src="currentForm.selectedIcon?.colors?.icon_path"
                    :alt="currentForm.selectedIcon?.name"
                  />
                  <a-icon v-else type="search" />
                </span>
              </template>
              <template #item="{ item }">
                <div
                  :title="item.name"
                  style="
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 8px;
                    border-radius: 2px;
                    border: 1px solid #d9d9d9;
                    cursor: pointer;
                  "
                >
                  <img
                    :src="item.colors?.icon_path"
                    :alt="item.name"
                    style="width: 16px; height: 16px"
                  />
                </div>
              </template>
            </InputSearchable>
          </a-form-model-item>

          <a-form-model-item label="Icon color" :colon="false">
            <input-color-picker
              :color="currentForm.iconColor"
              disableAlpha
              merged
              @input="(val) => (currentForm.iconColor = val)"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false">
            <template #label>
              <span>Show tournaments</span>
              <a-tooltip
                slot="suffix"
                title="Visibility of which tournaments matches are going to (tournament names)"
              >
                <a-icon type="info-circle" class="text-primary" />
              </a-tooltip>
            </template>
            <a-radio-group v-model="currentForm.showTournaments">
              <a-radio :value="true">Yes</a-radio>
              <a-radio :value="false">No</a-radio>
            </a-radio-group>
          </a-form-model-item>

          <a-form-model-item :colon="false">
            <template #label>
              <span>Show all category titles</span>
              <a-tooltip
                slot="suffix"
                title="Visibility of match category names"
              >
                <a-icon type="info-circle" class="text-primary" />
              </a-tooltip>
            </template>
            <a-radio-group :value="showAllCategoriesTitle" @change="handleToggleCategoriesTitle">
              <a-radio :value="true">Yes</a-radio>
              <a-radio :value="false">No</a-radio>
            </a-radio-group>
          </a-form-model-item>
        </a-form-model>
      </div>
    </template>
    <template slot="footer">
      <div class="footer">
        <a-button
          v-if="isEditMode"
          key="Delete"
          type="danger"
          @click="$emit('delete', editId)"
          >Delete</a-button
        >
        <div style="margin-left: auto">
          <a-button key="cancel" @click="$emit('close')"> Cancel </a-button>
          <a-button key="save" type="primary" @click="handleSubmit">
            Save
          </a-button>
        </div>
      </div>
    </template>
  </a-modal>
</template>
<script>
import InputColorPicker from '@/components/custom/colorPicker/inputColorPicker'
import InputSearchable from '@/components/custom/inputSearchable.vue'
import LanguageSelect from '@/components/custom/languageSelect.vue'
import { DEFAULT_LANG } from '@/constants/lang'

import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'

import isEqual from 'lodash/isEqual'

const FORM_FIELDS = {
  title: '',
  iconField: null,
  selectedIcon: null,
  iconColor: '#000000',
  showTournaments: true,
  showCategoryTitle: true,
}

export default {
  components: {
    InputColorPicker,
    InputSearchable,
    LanguageSelect,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    data: {
      type: Object,
    },
    initLang: {
      type: String,
      default: 'ru',
    },
  },
  data() {
    return {
      form: {
        ru: { ...FORM_FIELDS },
      },
      rules: {
        title: [
          {
            required: true,
            message: 'Please enter a category title',
            trigger: 'blur',
          },
        ],
        selectedIcon: [
          { required: true, message: 'Please select an icon', trigger: 'blur' },
        ],
      },
      selectedLanguage: 'ru',
      validationResult: true,
    }
  },
  computed: {
    ...mapGetters(['showAllCategoriesTitle']),
    ...mapState({
      langs: (state) => state.langs.langData,
      icons: (state) => state.customPages.sports,
    }),
    currentForm() {
      return this.form[this.selectedLanguage]
    },
    isEditMode() {
      return this.data && !!Object.entries(this.data).length
    },
    editId() {
      if (!this.isEditMode) {
        return null
      }

      return Object.values(this.form).filter(lang => lang.id)?.[0].id
    },
    isInitialForm() {
      return JSON.stringify(this.currentForm) === JSON.stringify(FORM_FIELDS)
    },
    formErrorState() {
      if (this.isInitialForm) {
        return null
      }
      return this.validationResult ? null : 'Form is not filled correctly'
    },
    filledLangs() {
      return this.langs.reduce((obj, { name }) => {
        obj[name] = !isEqual(this.form[name], FORM_FIELDS)
        return obj
      }, {})
    },
  },
  methods: {
    ...mapMutations(['SET_SHOW_ALL_CATEGORIES_TITLE']),
    ...mapActions(['debouncedFetchSports', 'fetchSports']),
    handleIconSearch(query) {
      this.debouncedFetchSports(query)
    },
    createFields() {
      return { ...JSON.parse(JSON.stringify(FORM_FIELDS)) }
    },
    initForm() {
      if (this.langs.length === 0) {
        this.form = {
          ru: this.createFields(),
        }

        return
      }

      this.form = this.langs.reduce((acc, lang) => {
        return {
          ...acc,
          [lang.name]: this.createFields(),
        }
      }, {})
    },
    fillForm() {
      if (!this.data) {
        return
      }

      const clonedData = JSON.parse(JSON.stringify(this.data))
      this.form = { ...this.form, ...clonedData }

      if (!this.form.ru?.title) {
        Object.entries(this.form).forEach(([key, value]) => {
          if (JSON.stringify(value) !== JSON.stringify(this.createFields())) {
            this.selectedLanguage = key
          }
        })
      }
    },
    handleSelectIcon(item) {
      this.currentForm.selectedIcon = item
    },
    handleSubmit() {
      this.$refs.categoryForm.validate((valid) => {
        if (valid) {
          const filteredForm = Object.fromEntries(
            Object.entries(this.form).filter(
              ([key, val]) =>
                JSON.stringify(val) !== JSON.stringify(FORM_FIELDS),
            ),
          )

          this.$emit(
            'submit',
            filteredForm,
            this.isEditMode ? this.editId : null,
          )
        }
      })
    },
    handleCopyFromDefault() {
      const defaultForm = this.form[DEFAULT_LANG]
      const { children, ...rest } = defaultForm ?? {}

      this.form[this.selectedLanguage] = JSON.parse(JSON.stringify({ ...rest, children: [] }))
    },
    validateForm() {
      if (!this.$refs.categoryForm) return
      this.$refs.categoryForm.validate((valid) => {
        this.validationResult = valid
      })
    },
    handleToggleCategoriesTitle(e) {
      this.SET_SHOW_ALL_CATEGORIES_TITLE(e?.target?.value ?? false)
    },
    handleClearLang(lang) {
      this.form[lang] = { ...FORM_FIELDS }

      this.$refs.categoryForm.clearValidate()
    },
  },
  created() {
    this.fetchSports()

    this.initForm()
    this.fillForm()

    this.selectedLanguage = this.initLang
  },
  watch: {
    data: {
      handler(val) {
        if (val) {
          this.form = JSON.parse(JSON.stringify(val))
        }
      },
      immediate: true,
    },
    currentForm: {
      deep: true,
      handler() {
        if (this.isInitialForm) {
          return
        }

        this.$nextTick(() => this.validateForm())
      },
    },
    selectedLanguage() {
      if (this.isInitialForm) {
        return
      }

      this.validateForm()
    },
  },
}
</script>
<style lang="scss" scoped>
.category-modal {
  position: relative;
  padding-top: 32px;

  ::v-deep .ant-form-item {
    margin-bottom: 16px;
  }

  ::v-deep .ant-form-item-control {
    line-height: 1;
  }

  ::v-deep .ant-form-item-label {
    overflow: visible;
  }

  ::v-deep .ant-tooltip-inner {
    font-weight: 400;
    white-space: initial;
  }

  ::v-deep .ant-form-item label {
    position: static;

    .anticon {
      margin-left: 4px;
    }
  }

  ::v-deep .ant-form-item-label {
    position: static;
  }

  .prefix {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    line-height: 1 !important;
    color: #BFBFBF;

    img {
      width: 18px;
      height: 18px;
      filter: brightness(0);
    }
  }

  ::v-deep .ant-input-affix-wrapper .ant-input:not(:first-child) {
    padding-left: 32px;
  }

  ::v-deep .ant-input-affix-wrapper .ant-input-prefix {
    pointer-events: none;
    left: 10px;
  }

  ::v-deep .dropdown {
    display: block;
  }

  ::v-deep .dropdown--grid {
    padding: 16px !important;
    scrollbar-gutter: stable both-edges;
    scrollbar-width: thin;
  }

  ::v-deep .dropdown--grid .items {
    gap: 9px;
    grid-template-columns: repeat(auto-fit, minmax(31px, 1fr));
  }

  ::v-deep .dropdown__item div {
    overflow: hidden;
    transition: all 0.3s;
  }

  ::v-deep .dropdown__item div:hover {
    background-color: #f2f2f2;
  }

  ::v-deep .dropdown__item img {
    filter: brightness(0);
  }
}

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
